import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Code, Copy, Check, Edit3, Download, Share2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiPath, appUrl } from '../utils/appPaths';
import { normalizeAffiliateLinks } from '../utils/affiliateLinks';
import { PUBLIC_CONTENT_PAGE_MAX_WIDTH } from '../utils/publicPageLayout';
import { deriveWiringGuideSeoDescription, deriveWiringGuideSeoKeywords, usePageSeo } from '../utils/seo';
import { normalizeWiringGuideSvgBackgroundElement } from '../utils/wiringGuideSvg';
import { downloadSvgElementAsPng } from '../utils/svgExport';
import SVGViewer from '../components/SVGViewer';

const WiringGuideContainer = styled.div`
  width: min(${PUBLIC_CONTENT_PAGE_MAX_WIDTH}, calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
  padding: clamp(24px, 4vw, 48px) 0 clamp(24px, 3vw, 36px);
`;

const Header = styled.div`
  display: grid;
  gap: 16px;
  margin-bottom: clamp(24px, 4vw, 40px);
`;

const Title = styled.h1`
  color: #1f2937;
  font-size: clamp(2rem, 3vw, 2.5rem);
  font-weight: 700;
  line-height: 1.1;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1.1rem;
`;

const Description = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: clamp(18px, 2vw, 24px);

  p {
    color: #374151;
    line-height: 1.6;
    margin: 0;
  }
`;

const WiringGuideLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(320px, 2fr);
  gap: clamp(24px, 3vw, 40px);
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const GuideCanvasColumn = styled.div`
  min-width: 0;
`;

const SidebarColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (min-width: 1101px) {
    position: sticky;
    top: 104px;
  }
`;

const ActionButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const actionButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    transform: translateY(-1px);
  }
`;

const PinConnections = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
`;

const PinConnectionsTitle = styled.h3`
  color: #1f2937;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  display: grid;
  gap: 4px;

  span {
    font-size: 13px;
    font-weight: 400;
    color: #6b7280;
  }
`;

const PinTableScroll = styled.div`
  overflow: auto;

  @media (min-width: 1101px) {
    max-height: calc(100vh - 192px);
  }
`;

const PinTable = styled.table`
  width: 100%;
  min-width: 360px;
  border-collapse: collapse;
  font-size: 14px;
`;

const PinTableHeader = styled.thead`
  background: #f9fafb;
`;

const PinTableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: #f9fafb;
  z-index: 1;
`;

const PinTableBody = styled.tbody``;

const PinTableRow = styled.tr`
  cursor: pointer;
  background: ${props => props.$selected ? '#fff7ed' : 'transparent'};

  &:hover {
    background: ${props => props.$selected ? '#ffedd5' : '#f9fafb'};
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const PinTableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;

  &:first-child,
  &:last-child {
    font-weight: 500;
    color: #1f2937;
  }
`;

const PinConnection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 12px;
`;

const PinArrow = styled.span`
  color: #9ca3af;
  font-weight: 700;
`;

const TutorialLinksPanel = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px 24px;
  display: grid;
  gap: 14px;
`;

const DiagramBoardsPanel = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px 24px;
  display: grid;
  gap: 14px;
`;

const DiagramBoardsTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 1.2rem;
  font-weight: 600;
`;

const DiagramBoardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DiagramBoardCard = styled(Link)`
  display: block;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(37, 99, 235, 0.08);
    border-color: #93c5fd;
  }
`;

const DiagramBoardRole = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;
`;

const DiagramBoardName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const DiagramBoardPath = styled.div`
  font-size: 13px;
  color: #2563eb;
`;

const TutorialLinksTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 1.2rem;
  font-weight: 600;
`;

const TutorialLinksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TutorialLinkCard = styled.a`
  display: block;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(37, 99, 235, 0.08);
    border-color: #93c5fd;
  }
`;

const TutorialLinkLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 4px;
`;

const TutorialLinkUrl = styled.div`
  font-size: 13px;
  color: #64748b;
  word-break: break-all;
`;

const WiringGuideViewer = styled(SVGViewer)`
  height: clamp(520px, 74vh, 940px);

  @media (max-width: 768px) {
    height: clamp(360px, 58vh, 640px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 1.2rem;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 1.2rem;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 20px;
`;

const EmbedButton = styled.button`
  ${actionButtonStyles}
  background: #f59e0b;

  &:hover {
    background: #d97706;
  }
`;

const EditButton = styled(Link)`
  ${actionButtonStyles}
  background: #3b82f6;
  text-decoration: none;

  &:hover {
    background: #2563eb;
    color: white;
  }
`;

const ExportButton = styled.button`
  ${actionButtonStyles}
  background: #0f766e;

  &:hover {
    background: #115e59;
  }
`;

const ShareButton = styled.button`
  ${actionButtonStyles}
  background: #7c3aed;

  &:hover {
    background: #6d28d9;
  }
`;

const EmbedModal = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const EmbedModalContent = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const EmbedModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const EmbedModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
`;

const EmbedModalClose = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;

  &:hover {
    color: #374151;
  }
`;

const EmbedCodeContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  position: relative;
`;

const EmbedCode = styled.pre`
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-all;
`;

const CopyButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &.copied {
    background: #10b981;
    color: white;
    border-color: #10b981;
  }
`;

const EmbedPreview = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 16px;
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.9rem;
`;

const EXPORT_SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const EXPORT_TABLE_FONT_FAMILY = 'Inter, "Segoe UI", Arial, sans-serif';

const clampExportValue = (value, min, max) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return min;
  }

  return Math.min(Math.max(numericValue, min), max);
};

const createExportSvgElement = (tagName, attributes = {}) => {
  const element = document.createElementNS(EXPORT_SVG_NAMESPACE, tagName);

  Object.entries(attributes).forEach(([name, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      element.setAttribute(name, String(value));
    }
  });

  return element;
};

const estimateExportTextWidth = (text, fontSize, fontWeight = 500) => {
  const normalizedText = String(text || '');
  const widthFactor = Number(fontWeight) >= 600 ? 0.62 : 0.56;
  return normalizedText.length * fontSize * widthFactor;
};

const truncateExportText = (text, maxChars) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) {
    return '';
  }

  if (!Number.isFinite(maxChars) || maxChars <= 1 || normalizedText.length <= maxChars) {
    return normalizedText;
  }

  return `${normalizedText.slice(0, Math.max(1, maxChars - 1)).trimEnd()}...`;
};

const wrapExportTableText = (text, maxWidth, fontSize, maxLines = 2) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) {
    return ['-'];
  }

  const safeMaxWidth = Math.max(48, Number(maxWidth) || 0);
  const estimatedCharWidth = Math.max(5.5, fontSize * 0.56);
  const maxCharsPerLine = Math.max(4, Math.floor(safeMaxWidth / estimatedCharWidth));
  const rawTokens = normalizedText.split(/\s+/).filter(Boolean);
  const tokens = rawTokens.flatMap((token) => {
    if (token.length <= maxCharsPerLine) {
      return [token];
    }

    const chunks = token.match(new RegExp(`.{1,${maxCharsPerLine}}`, 'g'));
    return chunks && chunks.length ? chunks : [token];
  });

  const lines = [];
  let currentLine = '';

  tokens.forEach((token) => {
    const candidate = currentLine ? `${currentLine} ${token}` : token;

    if (!currentLine || estimateExportTextWidth(candidate, fontSize) <= safeMaxWidth) {
      currentLine = candidate;
      return;
    }

    lines.push(currentLine);
    currentLine = token;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  if (lines.length <= maxLines) {
    return lines;
  }

  const limitedLines = lines.slice(0, maxLines);
  limitedLines[maxLines - 1] = truncateExportText(limitedLines[maxLines - 1], maxCharsPerLine - 1);
  return limitedLines;
};

const createMultilineExportText = ({
  x,
  startY,
  lines,
  fontSize,
  lineHeight,
  fill = '#111827',
  fontWeight = 500,
  textAnchor = 'start',
}) => {
  const text = createExportSvgElement('text', {
    x: Number(x.toFixed(2)),
    y: Number(startY.toFixed(2)),
    fill,
    'font-size': fontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': fontWeight,
    'text-anchor': textAnchor,
  });

  lines.forEach((line, index) => {
    const tspan = createExportSvgElement('tspan', {
      x: Number(x.toFixed(2)),
      y: Number((startY + (index * lineHeight)).toFixed(2)),
    });
    tspan.textContent = line;
    text.appendChild(tspan);
  });

  return text;
};

const appendConnectionsTableToExportSvg = (svgElement, {
  connections = [],
  selectedConnectionIndex = null,
  viewBox,
}) => {
  if (!(svgElement instanceof SVGElement) || !viewBox) {
    return;
  }

  const safeConnections = Array.isArray(connections) ? connections : [];
  const cardMaxWidth = Math.max(240, viewBox.width - 48);
  const cardWidth = Math.min(cardMaxWidth, 1120);
  const sectionGap = clampExportValue(viewBox.height * 0.07, 42, 88);
  const cardX = viewBox.minX + ((viewBox.width - cardWidth) / 2);
  const cardY = viewBox.minY + viewBox.height + sectionGap;
  const cardPaddingX = clampExportValue(cardWidth * 0.04, 18, 30);
  const cardPaddingY = clampExportValue(viewBox.height * 0.03, 18, 28);
  const titleFontSize = clampExportValue(cardWidth * 0.024, 18, 24);
  const subtitleFontSize = clampExportValue(titleFontSize - 6, 11, 14);
  const headerFontSize = clampExportValue(titleFontSize - 5, 12, 15);
  const bodyFontSize = clampExportValue(titleFontSize - 4, 13, 16);
  const bodyLineHeight = Math.round(bodyFontSize * 1.35);
  const titleBlockHeight = titleFontSize + subtitleFontSize + 14;
  const columnGap = clampExportValue(cardWidth * 0.04, 28, 44);
  const contentWidth = cardWidth - (cardPaddingX * 2);
  const columnWidth = Math.max(120, (contentWidth - columnGap) / 2);
  const headerRowHeight = Math.max(38, headerFontSize + 22);
  const headerRowY = cardY + cardPaddingY + titleBlockHeight + 12;
  const bodyStartY = headerRowY + headerRowHeight;

  const rowLayouts = safeConnections.map((connection, index) => {
    const sensorLines = wrapExportTableText(connection?.sensorPin, columnWidth - 10, bodyFontSize, 2);
    const microcontrollerLines = wrapExportTableText(connection?.microcontrollerPin, columnWidth - 10, bodyFontSize, 2);
    const maxLineCount = Math.max(sensorLines.length, microcontrollerLines.length);
    const rowHeight = Math.max(48, (maxLineCount * bodyLineHeight) + 22);

    return {
      index,
      sensorLines,
      microcontrollerLines,
      rowHeight,
      isSelected: selectedConnectionIndex === index,
    };
  });

  const rowsHeight = rowLayouts.length
    ? rowLayouts.reduce((total, row) => total + row.rowHeight, 0)
    : 64;
  const cardHeight = cardPaddingY + titleBlockHeight + 12 + headerRowHeight + rowsHeight + cardPaddingY;
  const leftColumnX = cardX + cardPaddingX;
  const rightColumnX = leftColumnX + columnWidth + columnGap;

  svgElement.style.setProperty('overflow', 'visible', 'important');

  const tableGroup = createExportSvgElement('g', {
    'data-export-connections-table': '1',
  });

  const cardBackground = createExportSvgElement('rect', {
    x: Number(cardX.toFixed(2)),
    y: Number(cardY.toFixed(2)),
    width: Number(cardWidth.toFixed(2)),
    height: Number(cardHeight.toFixed(2)),
    rx: 18,
    fill: '#ffffff',
    stroke: '#e5e7eb',
    'stroke-width': 1.5,
  });
  tableGroup.appendChild(cardBackground);

  const titleY = cardY + cardPaddingY + titleFontSize;
  const title = createExportSvgElement('text', {
    x: Number(leftColumnX.toFixed(2)),
    y: Number(titleY.toFixed(2)),
    fill: '#111827',
    'font-size': titleFontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': 700,
  });
  title.textContent = 'Pin Connections';
  tableGroup.appendChild(title);

  const subtitle = createExportSvgElement('text', {
    x: Number(leftColumnX.toFixed(2)),
    y: Number((titleY + subtitleFontSize + 10).toFixed(2)),
    fill: '#6b7280',
    'font-size': subtitleFontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': 500,
  });
  subtitle.textContent = rowLayouts.length
    ? `${rowLayouts.length} saved connection${rowLayouts.length === 1 ? '' : 's'}`
    : 'No saved connections';
  tableGroup.appendChild(subtitle);

  const headerBackground = createExportSvgElement('rect', {
    x: Number(leftColumnX.toFixed(2)),
    y: Number(headerRowY.toFixed(2)),
    width: Number(contentWidth.toFixed(2)),
    height: Number(headerRowHeight.toFixed(2)),
    rx: 10,
    fill: '#f8fafc',
  });
  tableGroup.appendChild(headerBackground);

  const headerBaselineY = headerRowY + (headerRowHeight / 2) + (headerFontSize * 0.35);
  tableGroup.appendChild(createExportSvgElement('text', {
    x: Number(leftColumnX.toFixed(2)),
    y: Number(headerBaselineY.toFixed(2)),
    fill: '#475569',
    'font-size': headerFontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': 700,
  }));
  tableGroup.lastChild.textContent = 'Sensor Board Pin';

  tableGroup.appendChild(createExportSvgElement('text', {
    x: Number(rightColumnX.toFixed(2)),
    y: Number(headerBaselineY.toFixed(2)),
    fill: '#475569',
    'font-size': headerFontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': 700,
  }));
  tableGroup.lastChild.textContent = 'Microcontroller Pin';

  if (!rowLayouts.length) {
    const emptyState = createExportSvgElement('text', {
      x: Number((cardX + (cardWidth / 2)).toFixed(2)),
      y: Number((bodyStartY + 38).toFixed(2)),
      fill: '#6b7280',
      'font-size': bodyFontSize,
      'font-family': EXPORT_TABLE_FONT_FAMILY,
      'font-weight': 500,
      'text-anchor': 'middle',
    });
    emptyState.textContent = 'No pin connections available.';
    tableGroup.appendChild(emptyState);
    svgElement.appendChild(tableGroup);
    return;
  }

  let currentRowY = bodyStartY;

  rowLayouts.forEach((row, rowIndex) => {
    const rowFill = row.isSelected
      ? '#fff7ed'
      : rowIndex % 2 === 0
        ? '#ffffff'
        : '#fcfdff';
    const rowBackground = createExportSvgElement('rect', {
      x: Number(leftColumnX.toFixed(2)),
      y: Number(currentRowY.toFixed(2)),
      width: Number(contentWidth.toFixed(2)),
      height: Number(row.rowHeight.toFixed(2)),
      rx: 10,
      fill: rowFill,
    });
    tableGroup.appendChild(rowBackground);

    const sensorTextHeight = ((row.sensorLines.length - 1) * bodyLineHeight) + bodyFontSize;
    const microTextHeight = ((row.microcontrollerLines.length - 1) * bodyLineHeight) + bodyFontSize;
    const sensorStartY = currentRowY + ((row.rowHeight - sensorTextHeight) / 2) + (bodyFontSize * 0.82);
    const microStartY = currentRowY + ((row.rowHeight - microTextHeight) / 2) + (bodyFontSize * 0.82);

    tableGroup.appendChild(createMultilineExportText({
      x: leftColumnX,
      startY: sensorStartY,
      lines: row.sensorLines,
      fontSize: bodyFontSize,
      lineHeight: bodyLineHeight,
      fill: '#111827',
      fontWeight: row.isSelected ? 700 : 600,
    }));

    tableGroup.appendChild(createMultilineExportText({
      x: rightColumnX,
      startY: microStartY,
      lines: row.microcontrollerLines,
      fontSize: bodyFontSize,
      lineHeight: bodyLineHeight,
      fill: '#111827',
      fontWeight: row.isSelected ? 700 : 600,
    }));

    currentRowY += row.rowHeight;
  });

  svgElement.appendChild(tableGroup);
};

const WiringGuide = () => {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [wiringGuide, setWiringGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const [selectedConnectionIndex, setSelectedConnectionIndex] = useState(null);
  const wiringGuidePreviewRef = useRef(null);
  const pinConnectionsRef = useRef(null);
  const shareFeedbackTimeoutRef = useRef(null);
  const tutorialLinks = useMemo(
    () => normalizeAffiliateLinks(wiringGuide?.tutorial_links),
    [wiringGuide?.tutorial_links]
  );
  const boardLinks = useMemo(() => {
    const uniqueBoards = new Set();

    return [
      {
        role: 'Sensor Board',
        name: wiringGuide?.sensor_name,
        slug: wiringGuide?.sensor_slug,
        id: wiringGuide?.sensor_board_id,
      },
      {
        role: 'Microcontroller',
        name: wiringGuide?.microcontroller_name,
        slug: wiringGuide?.microcontroller_slug,
        id: wiringGuide?.microcontroller_board_id,
      },
    ]
      .map((board) => {
        const identifier = board.slug || board.id;

        if (!board.name || !identifier) {
          return null;
        }

        const uniqueKey = String(identifier);

        if (uniqueBoards.has(uniqueKey)) {
          return null;
        }

        uniqueBoards.add(uniqueKey);

        return {
          ...board,
          path: `/board/${identifier}`,
        };
      })
      .filter(Boolean);
  }, [
    wiringGuide?.microcontroller_board_id,
    wiringGuide?.microcontroller_name,
    wiringGuide?.microcontroller_slug,
    wiringGuide?.sensor_board_id,
    wiringGuide?.sensor_name,
    wiringGuide?.sensor_slug,
  ]);
  const wiringGuideTitle = wiringGuide?.sensor_name && wiringGuide?.microcontroller_name
    ? `${wiringGuide.sensor_name} to ${wiringGuide.microcontroller_name} Wiring | MicroPinouts`
    : '';
  const seoDescription = deriveWiringGuideSeoDescription(wiringGuide);
  const seoKeywords = deriveWiringGuideSeoKeywords(wiringGuide);

  usePageSeo({
    title: wiringGuideTitle,
    description: seoDescription,
    keywords: seoKeywords,
    canonicalUrl: wiringGuide?.slug ? appUrl(`/wiring-guide/${wiringGuide.slug}`) : '',
  });

  const generateEmbedCode = () => {
    const embedUrl = appUrl(`/embed/wiring-guide/${slug}`);
    const iframeCode = `<iframe src="${embedUrl}" width="800" height="600" frameborder="0" style="border: 1px solid #e2e8f0; border-radius: 8px;"></iframe>`;
    setEmbedCode(iframeCode);
    setCopied(false);
    setShowEmbedModal(true);
  };

  const copyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy embed code:', err);
    }
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const didCopy = document.execCommand('copy');
      if (!didCopy) {
        throw new Error('Legacy clipboard copy failed.');
      }
    } finally {
      textArea.remove();
    }
  };

  const showShareFeedback = (status) => {
    setShareStatus(status);

    if (shareFeedbackTimeoutRef.current) {
      window.clearTimeout(shareFeedbackTimeoutRef.current);
    }

    shareFeedbackTimeoutRef.current = window.setTimeout(() => {
      setShareStatus('');
      shareFeedbackTimeoutRef.current = null;
    }, 2000);
  };

  const handleExportPng = async () => {
    const svgElement = wiringGuidePreviewRef.current?.querySelector('[data-svg-artwork-root="1"] > svg');
    if (!(svgElement instanceof SVGElement)) {
      return;
    }

    try {
      await downloadSvgElementAsPng(svgElement, {
        fileName: `${wiringGuide?.sensor_name || 'wiring-guide'}-to-${wiringGuide?.microcontroller_name || 'board'}-wiring-guide.png`,
        prepareClone: (clone, geometry) => {
          appendConnectionsTableToExportSvg(clone, {
            connections: wiringGuide?.connections,
            selectedConnectionIndex,
            viewBox: geometry?.viewBox,
          });
        },
      });
    } catch (error) {
      console.error('Failed to export wiring as PNG:', error);
    }
  };

  const handleShareLink = async () => {
    const shareUrl = window.location.href;
    const shareTitle = wiringGuide?.sensor_name && wiringGuide?.microcontroller_name
      ? `${wiringGuide.sensor_name} to ${wiringGuide.microcontroller_name} Wiring`
      : document.title || 'MicroPinouts';

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
        showShareFeedback('shared');
        return;
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
      }
    }

    try {
      await copyTextToClipboard(shareUrl);
      showShareFeedback('copied');
    } catch (error) {
      console.error('Failed to share wiring link:', error);
    }
  };

  const fetchWiringGuide = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(apiPath(`/wiring-guide/${slug}`));

      if (response.ok) {
        const data = await response.json();
        setWiringGuide(data);
        setError(null);
      } else {
        setError('Wiring not found');
      }
    } catch (err) {
      console.error('Error fetching wiring:', err);
      setError('Failed to load wiring');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchWiringGuide();
  }, [fetchWiringGuide]);

  useEffect(() => () => {
    if (shareFeedbackTimeoutRef.current) {
      window.clearTimeout(shareFeedbackTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const handleDocumentPointerDown = (event) => {
      if (selectedConnectionIndex === null) {
        return;
      }

      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (pinConnectionsRef.current?.contains(target)) {
        return;
      }

      setSelectedConnectionIndex(null);
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown);

    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown);
    };
  }, [selectedConnectionIndex]);

  const highlightedSvgContent = useMemo(() => {
    if (!wiringGuide?.svg_content) {
      return '';
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(wiringGuide.svg_content, 'image/svg+xml');

    if (doc.querySelector('parsererror')) {
      return wiringGuide.svg_content;
    }

    const svg = doc.querySelector('svg');

    if (!svg) {
      return wiringGuide.svg_content;
    }

    normalizeWiringGuideSvgBackgroundElement(svg);

    if (selectedConnectionIndex === null) {
      return new XMLSerializer().serializeToString(doc);
    }

    const style = doc.createElementNS('http://www.w3.org/2000/svg', 'style');

    style.textContent = `
      .wiring-guide-wire-selected {
        stroke-width: 7 !important;
        stroke-dasharray: 14 10;
        animation: wiringGuideWireFlow 0.8s linear infinite;
        filter: drop-shadow(0 0 5px var(--wire-highlight-color, currentColor))
                drop-shadow(0 0 9px var(--wire-highlight-color, currentColor));
      }

      .wiring-guide-connection-dimmed {
        opacity: 0.18;
      }

      @keyframes wiringGuideWireFlow {
        from {
          stroke-dashoffset: 24;
        }

        to {
          stroke-dashoffset: 0;
        }
      }
    `;

    svg.insertBefore(style, svg.firstChild);

    const connectionGroups = doc.querySelectorAll('.wiring-guide-connection');

    connectionGroups.forEach((group) => {
      const isSelected = group.getAttribute('data-connection-index') === String(selectedConnectionIndex);
      const path = group.querySelector('.wiring-guide-wire');

      if (!path) {
        return;
      }

      if (isSelected) {
        const originalStroke = path.getAttribute('stroke') || '#2563eb';

        group.setAttribute('opacity', '1');
        path.classList.add('wiring-guide-wire-selected');
        path.style.setProperty('--wire-highlight-color', originalStroke);
      } else {
        group.classList.add('wiring-guide-connection-dimmed');
      }
    });

    return new XMLSerializer().serializeToString(doc);
  }, [wiringGuide?.svg_content, selectedConnectionIndex]);

  if (loading) {
    return (
      <WiringGuideContainer>
        <LoadingContainer>Loading wiring...</LoadingContainer>
      </WiringGuideContainer>
    );
  }

  if (error) {
    return (
      <WiringGuideContainer>
        <ErrorContainer>{error}</ErrorContainer>
      </WiringGuideContainer>
    );
  }

  if (!wiringGuide) {
    return (
      <WiringGuideContainer>
        <ErrorContainer>Wiring not found</ErrorContainer>
      </WiringGuideContainer>
    );
  }

  const showEditButton = isAuthenticated && Boolean(wiringGuide.id);

  return (
    <WiringGuideContainer>
      <Header>
        <Title>{wiringGuide.sensor_name} to {wiringGuide.microcontroller_name}</Title>
        <Subtitle>Wiring</Subtitle>
        {wiringGuide.description && (
          <Description>
            <p>{wiringGuide.description}</p>
          </Description>
        )}
      </Header>

       <WiringGuideLayout>
        <GuideCanvasColumn ref={wiringGuidePreviewRef}>
          <WiringGuideViewer
            svgContent={highlightedSvgContent}
            initialZoom={1}
            minZoom={0.2}
            maxZoom={3}
            enablePan={true}
            enableZoom={true}
            enableWheelZoom={false}
            showWheelZoomToggle={true}
          />
        </GuideCanvasColumn>

        <SidebarColumn>
          <ActionButtonRow>
            <ExportButton
              type="button"
              onClick={handleExportPng}
              title="Export as PNG"
              aria-label="Export as PNG"
            >
              <Download size={18} />
            </ExportButton>
            <ShareButton
              type="button"
              onClick={handleShareLink}
              title={shareStatus === 'shared' ? 'Link shared' : shareStatus === 'copied' ? 'Link copied' : 'Share link'}
              aria-label={shareStatus === 'shared' ? 'Link shared' : shareStatus === 'copied' ? 'Link copied' : 'Share link'}
            >
              {shareStatus ? <Check size={18} /> : <Share2 size={18} />}
            </ShareButton>
            <EmbedButton
              type="button"
              onClick={generateEmbedCode}
              title="Embed wiring"
              aria-label="Embed wiring"
            >
              <Code size={18} />
            </EmbedButton>
            {showEditButton && (
              <EditButton
                to={`/admin?tab=wiring&editGuide=${wiringGuide.id}`}
                title="Edit wiring"
                aria-label="Edit wiring"
              >
                <Edit3 size={18} />
              </EditButton>
            )}
          </ActionButtonRow>

          
          <PinConnections ref={pinConnectionsRef}>
            <PinConnectionsTitle>Pin Connections
              <span>Click a row to highlight the wire</span>
              </PinConnectionsTitle>
            <PinTableScroll>
              <PinTable>
                <PinTableHeader>
                  <tr>
                    <PinTableHeaderCell>Sensor Board Pin</PinTableHeaderCell>
                    <PinTableHeaderCell>Connection</PinTableHeaderCell>
                    <PinTableHeaderCell>Microcontroller Pin</PinTableHeaderCell>
                  </tr>
                </PinTableHeader>
                <PinTableBody>
                  {wiringGuide.connections?.map((connection, index) => (
                    <PinTableRow
                      key={index}
                      $selected={selectedConnectionIndex === index}
                      onClick={() => setSelectedConnectionIndex(
                        selectedConnectionIndex === index ? null : index
                      )}
                    >
                      <PinTableCell>{connection.sensorPin}</PinTableCell>
                      <PinTableCell>
                        <PinConnection>
                          <PinArrow>&lt;-&gt;</PinArrow>
                        </PinConnection>
                      </PinTableCell>
                      <PinTableCell>{connection.microcontrollerPin}</PinTableCell>
                    </PinTableRow>
                  ))}
                </PinTableBody>
              </PinTable>
            </PinTableScroll>
          </PinConnections>

          {boardLinks.length > 0 && (
            <DiagramBoardsPanel>
              <DiagramBoardsTitle>Boards in This Diagram</DiagramBoardsTitle>
              <DiagramBoardsList>
                {boardLinks.map((boardLink) => (
                  <DiagramBoardCard key={boardLink.path} to={boardLink.path}>
                    <DiagramBoardRole>{boardLink.role}</DiagramBoardRole>
                    <DiagramBoardName>{boardLink.name}</DiagramBoardName>
                    <DiagramBoardPath>Open pinout page</DiagramBoardPath>
                  </DiagramBoardCard>
                ))}
              </DiagramBoardsList>
            </DiagramBoardsPanel>
          )}

          {tutorialLinks.length > 0 && (
            <TutorialLinksPanel>
              <TutorialLinksTitle>Tutorial Links</TutorialLinksTitle>
              <TutorialLinksList>
                {tutorialLinks.map((tutorialLink, index) => (
                  <TutorialLinkCard
                    key={`tutorial-link-${index}`}
                    href={tutorialLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <TutorialLinkLabel>{tutorialLink.label}</TutorialLinkLabel>
                    <TutorialLinkUrl>{tutorialLink.url}</TutorialLinkUrl>
                  </TutorialLinkCard>
                ))}
              </TutorialLinksList>
            </TutorialLinksPanel>
          )}

        </SidebarColumn>
      </WiringGuideLayout>

      <AnimatePresence>
        {showEmbedModal && (
          <EmbedModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(event) => event.target === event.currentTarget && setShowEmbedModal(false)}
          >
            <EmbedModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <EmbedModalHeader>
                <EmbedModalTitle>Embed This Wiring</EmbedModalTitle>
                <EmbedModalClose onClick={() => setShowEmbedModal(false)}>
                  ×
                </EmbedModalClose>
              </EmbedModalHeader>

              <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                Copy the code below to embed this wiring on your website:
              </p>

              <EmbedCodeContainer>
                <CopyButton
                  onClick={copyEmbedCode}
                  className={copied ? 'copied' : ''}
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied!' : 'Copy'}
                </CopyButton>
                <EmbedCode>{embedCode}</EmbedCode>
              </EmbedCodeContainer>

              <div style={{ marginTop: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Preview:</h4>
                <EmbedPreview>
                  <iframe
                    src={appUrl(`/embed/wiring-guide/${slug}`)}
                    width="100%"
                    height="400"
                    frameBorder="0"
                    style={{ border: 'none' }}
                    title="Wiring Embed Preview"
                  />
                </EmbedPreview>
              </div>
            </EmbedModalContent>
          </EmbedModal>
        )}
      </AnimatePresence>
      <Copyright>
        <strong>&copy; 2026 micropinouts.com</strong><br />
        All rights reserved.<br />
        Educational use and community sharing encouraged under applicable open-source licenses.
      </Copyright>
    </WiringGuideContainer>
  );
};

export default WiringGuide;
